import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Calendar, MapPin, Ticket, Trophy } from "lucide-react";
import { fetchList } from "../api";
import { PageHero, Loader } from "../components/Shared";

const fmtDate = (d) => {
  if (!d) return "Date TBA";
  const dt = /^\d{4}-\d{2}-\d{2}$/.test(d) ? new Date(d + "T00:00:00") : new Date(d);
  if (isNaN(dt)) return "Date TBA";
  return dt.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric" });
};

export default function Events() {
  const [events, setEvents] = useState(null);
  const [tab, setTab] = useState("upcoming");

  useEffect(() => { fetchList("events").then(setEvents).catch(() => setEvents([])); }, []);
  if (!events) return <Loader />;

  const shown = events.filter((e) => e.status === tab);

  return (
    <div data-testid="events-page">
      <PageHero kicker="Catch The Action" title="Upcoming Events" image="https://images.unsplash.com/photo-1470229722913-7c0e2dbbafd3?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000" />
      <section className="px-6 md:px-12 lg:px-20 pb-24">
        <div className="flex gap-3 mb-10">
          {["upcoming", "past"].map((t) => (
            <button key={t} data-testid={`events-tab-${t}`} onClick={() => setTab(t)}
              className={`font-display text-xl uppercase tracking-wide px-6 py-2 border-2 transition-colors ${tab === t ? "bg-brand border-brand text-black" : "border-zinc-700 text-zinc-400 hover:border-white hover:text-white"}`}>
              {t}
            </button>
          ))}
        </div>

        <div className="space-y-6">
          {shown.map((ev, i) => (
            <motion.div key={ev.id} data-testid={`event-card-${ev.id}`} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: (i % 6) * 0.08 }}
              className="grid grid-cols-1 md:grid-cols-12 gap-6 bg-[#111] border border-zinc-800 overflow-hidden group">
              <div className="md:col-span-4 relative aspect-video md:aspect-auto overflow-hidden bg-black flex items-center justify-center">
                <img src={ev.poster} alt={ev.title} className={`w-full h-full group-hover:scale-105 transition-transform duration-500 ${ev.poster_fit === "cover" ? "object-cover object-center" : "object-contain"}`} />
              </div>
              <div className="md:col-span-8 p-6 md:p-8 flex flex-col md:flex-row md:items-start gap-6">
                <div className="flex-1">
                  <span className="font-mono text-xs uppercase tracking-widest text-brand flex items-center gap-2"><Calendar size={14} /> {fmtDate(ev.date)}{ev.time ? ` · ${ev.time}` : ""}</span>
                  <h3 className="font-display text-4xl uppercase tracking-tight mt-2 leading-none">{ev.title}</h3>
                  <p className="flex items-center gap-2 text-zinc-400 mt-2"><MapPin size={15} /> {[ev.venue, ev.city].filter(Boolean).join(", ") || "Venue TBA"}</p>
                  {ev.main_event && <p className="text-zinc-500 mt-1 text-sm font-mono uppercase tracking-wide">Main Event: <span className="text-white">{ev.main_event}</span></p>}
                  {ev.description && <p className="text-zinc-500 mt-3 max-w-xl whitespace-pre-line">{ev.description}</p>}
                  {ev.results && ev.results.length > 0 && (
                    <div className="mt-5 border-t border-zinc-800 pt-4" data-testid={`event-results-${ev.id}`}>
                      <div className="flex items-center gap-2 text-brand mb-4">
                        <Trophy size={16} />
                        <span className="font-mono text-xs uppercase tracking-widest">Match Results</span>
                      </div>
                      <ul className="space-y-3">
                        {ev.results.map((r, idx) => {
                          const isNew = /\bNEW\b/.test(r) || /Champions?!/i.test(r);
                          const isMain = idx === ev.results.length - 1;
                          return (
                            <li key={idx} className="flex gap-4 items-start">
                              <span className="font-display text-2xl text-zinc-600 leading-none w-7 shrink-0 pt-0.5">{String(idx + 1).padStart(2, "0")}</span>
                              <div className="border-l-2 border-zinc-800 pl-4">
                                <p className="text-zinc-200 leading-snug">{r}</p>
                                {(isNew || isMain) && (
                                  <div className="flex flex-wrap gap-2 mt-2">
                                    {isMain && <span className="border border-zinc-600 text-zinc-300 font-mono text-[9px] uppercase tracking-widest px-2 py-1">Main Event</span>}
                                    {isNew && <span className="bg-brand text-black font-mono text-[9px] uppercase tracking-widest px-2 py-1 font-bold">New Champion</span>}
                                  </div>
                                )}
                              </div>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  )}
                </div>
                {tab === "upcoming" && (
                  <div className="text-center shrink-0">
                    <div className="border-2 border-brand text-brand font-display text-2xl uppercase px-8 py-4 flex items-center gap-2" data-testid={`free-admission-${ev.id}`}>
                      <Ticket size={20} /> Free Admission
                    </div>
                    <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mt-2">All fans welcome</p>
                  </div>
                )}
              </div>
            </motion.div>
          ))}
          {shown.length === 0 && <p className="text-zinc-500">No {tab} events.</p>}
        </div>
      </section>
    </div>
  );
}
